import { useCallback } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  NodeProps,
  Handle,
  Position
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { motion } from 'framer-motion';
import {
  Server,
  Radio,
  Building2,
  CircleDot
} from 'lucide-react';
import { NodeLevel, LinkState } from '../../types/protocol';

// Custom node component for network nodes
function NetworkNode({ data }: NodeProps) {
  const { level, label, logicalId, state, isActive } = data;

  const icons: Record<NodeLevel, React.ReactNode> = {
    [NodeLevel.HOST]: <Server className="w-6 h-6" />,
    [NodeLevel.CENTRAL]: <Building2 className="w-5 h-5" />,
    [NodeLevel.REGULATOR]: <Radio className="w-5 h-5" />,
    [NodeLevel.SUBREGULATOR]: <CircleDot className="w-4 h-4" />
  };

  const colors: Record<NodeLevel, string> = {
    [NodeLevel.HOST]: 'bg-purple-600 border-purple-400',
    [NodeLevel.CENTRAL]: 'bg-blue-600 border-blue-400',
    [NodeLevel.REGULATOR]: 'bg-green-600 border-green-400',
    [NodeLevel.SUBREGULATOR]: 'bg-cyan-600 border-cyan-400'
  };

  const stateColors: Record<LinkState, string> = {
    [LinkState.UNKNOWN]: 'ring-gray-500',
    [LinkState.OK]: 'ring-green-500',
    [LinkState.ERROR]: 'ring-red-500',
    [LinkState.COUNTING_ERRORS]: 'ring-orange-500',
    [LinkState.COUNTING_OK]: 'ring-yellow-500'
  };

  return (
    <>
      {/* Input handle (top) */}
      {level !== NodeLevel.HOST && (
        <Handle
          type="target"
          position={Position.Top}
          className="w-2 h-2 bg-gray-400"
        />
      )}

      <motion.div
        className={`
          px-3 py-2 rounded-lg border-2
          ${colors[level]}
          ${state ? `ring-2 ${stateColors[state]}` : ''}
          cursor-pointer
          min-w-[80px]
        `}
        whileHover={{ scale: 1.05 }}
        animate={isActive ? {
          boxShadow: ['0 0 0 0 rgba(34, 197, 94, 0)', '0 0 0 10px rgba(34, 197, 94, 0.3)', '0 0 0 0 rgba(34, 197, 94, 0)']
        } : {}}
        transition={isActive ? { duration: 1.5, repeat: Infinity } : {}}
      >
        <div className="flex items-center gap-2 text-white">
          {icons[level]}
          <div className="flex flex-col">
            <span className="text-xs font-medium">{label}</span>
            <span className="text-[10px] opacity-70">ID: {logicalId}</span>
          </div>
        </div>
      </motion.div>

      {/* Output handle (bottom) */}
      {level !== NodeLevel.SUBREGULATOR && (
        <Handle
          type="source"
          position={Position.Bottom}
          className="w-2 h-2 bg-gray-400"
        />
      )}
    </>
  );
}

const nodeTypes = {
  networkNode: NetworkNode
};

interface NetworkTopologyProps {
  className?: string;
}

// Demo data - will be replaced with real state
const initialNodes: Node[] = [
  // HOST level
  {
    id: 'host-0',
    type: 'networkNode',
    position: { x: 300, y: 0 },
    data: {
      level: NodeLevel.HOST,
      label: 'HOST',
      logicalId: 1,
      state: LinkState.OK,
      isActive: true
    }
  },
  // CENTRAL level
  {
    id: 'central-1',
    type: 'networkNode',
    position: { x: 150, y: 120 },
    data: {
      level: NodeLevel.CENTRAL,
      label: 'Central 1',
      logicalId: 100,
      state: LinkState.OK,
      isActive: false
    }
  },
  {
    id: 'central-2',
    type: 'networkNode',
    position: { x: 450, y: 120 },
    data: {
      level: NodeLevel.CENTRAL,
      label: 'Central 2',
      logicalId: 101,
      state: LinkState.OK,
      isActive: false
    }
  },
  // REGULATOR level
  {
    id: 'reg-1',
    type: 'networkNode',
    position: { x: 50, y: 240 },
    data: {
      level: NodeLevel.REGULATOR,
      label: 'Reg 1',
      logicalId: 1001,
      state: LinkState.OK,
      isActive: false
    }
  },
  {
    id: 'reg-2',
    type: 'networkNode',
    position: { x: 200, y: 240 },
    data: {
      level: NodeLevel.REGULATOR,
      label: 'Reg 2',
      logicalId: 1002,
      state: LinkState.COUNTING_ERRORS,
      isActive: false
    }
  },
  {
    id: 'reg-3',
    type: 'networkNode',
    position: { x: 400, y: 240 },
    data: {
      level: NodeLevel.REGULATOR,
      label: 'Reg 3',
      logicalId: 1003,
      state: LinkState.OK,
      isActive: false
    }
  },
  {
    id: 'reg-4',
    type: 'networkNode',
    position: { x: 550, y: 240 },
    data: {
      level: NodeLevel.REGULATOR,
      label: 'Reg 4',
      logicalId: 1004,
      state: LinkState.ERROR,
      isActive: false
    }
  }
];

const initialEdges: Edge[] = [
  // HOST to CENTRALs
  {
    id: 'e-host-c1',
    source: 'host-0',
    target: 'central-1',
    animated: true,
    style: { stroke: '#22c55e', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#22c55e' }
  },
  {
    id: 'e-host-c2',
    source: 'host-0',
    target: 'central-2',
    animated: true,
    style: { stroke: '#22c55e', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#22c55e' }
  },
  // CENTRAL 1 to REGULATORs
  {
    id: 'e-c1-r1',
    source: 'central-1',
    target: 'reg-1',
    animated: true,
    style: { stroke: '#22c55e', strokeWidth: 2 }
  },
  {
    id: 'e-c1-r2',
    source: 'central-1',
    target: 'reg-2',
    animated: false,
    style: { stroke: '#f59e0b', strokeWidth: 2, strokeDasharray: '5,5' }
  },
  // CENTRAL 2 to REGULATORs
  {
    id: 'e-c2-r3',
    source: 'central-2',
    target: 'reg-3',
    animated: true,
    style: { stroke: '#22c55e', strokeWidth: 2 }
  },
  {
    id: 'e-c2-r4',
    source: 'central-2',
    target: 'reg-4',
    animated: false,
    style: { stroke: '#ef4444', strokeWidth: 2 }
  }
];

export function NetworkTopology({ className }: NetworkTopologyProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  return (
    <div className={`bg-gray-900 rounded-lg border border-gray-700 ${className}`}>
      <div className="bg-gray-800 px-3 py-2 border-b border-gray-700">
        <span className="text-sm font-medium text-gray-300">Topología de Red</span>
      </div>
      <div className="h-[400px]">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          proOptions={{ hideAttribution: true }}
        >
          <Background color="#374151" gap={16} />
          <Controls className="bg-gray-800 border-gray-700" />
          <MiniMap
            nodeColor={(node) => {
              const level = node.data?.level as NodeLevel;
              switch (level) {
                case NodeLevel.HOST: return '#9333ea';
                case NodeLevel.CENTRAL: return '#2563eb';
                case NodeLevel.REGULATOR: return '#16a34a';
                case NodeLevel.SUBREGULATOR: return '#0891b2';
                default: return '#6b7280';
              }
            }}
            className="bg-gray-800 border-gray-700"
          />
        </ReactFlow>
      </div>
      {/* Legend */}
      <div className="px-3 py-2 border-t border-gray-700 flex gap-4 text-xs text-gray-400">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-green-500"></div>
          <span>OK</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-orange-500"></div>
          <span>Contando errores</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-red-500"></div>
          <span>Error</span>
        </div>
      </div>
    </div>
  );
}
